import { apiFetch } from "./api";

export interface PartnerCompany {
  id: number;
  name: string;
  eeRegistryCode: string | null;
  euVatId: string | null;
  billingEmail: string | null;
  createdAt: number;
}

export interface PartnerCompanyInput {
  name?: string;
  eeRegistryCode?: string | null;
  euVatId?: string | null;
  billingEmail?: string | null;
}

export class PartnerCompanyApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

function extractMessage(body: unknown): string | null {
  if (typeof body === "object" && body !== null && "message" in body) {
    const m = body.message;
    return typeof m === "string" ? m : null;
  }
  return null;
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const message = extractMessage(body) ?? `partner-companies api ${res.status}`;
    throw new PartnerCompanyApiError(res.status, body, message);
  }
  const data: unknown = await res.json();
  return data as T;
}

export async function listPartnerCompanies(q?: string): Promise<PartnerCompany[]> {
  const qs = q?.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
  return readJson<PartnerCompany[]>(await apiFetch(`/api/admin/companies${qs}`));
}

export async function createPartnerCompany(input: PartnerCompanyInput): Promise<PartnerCompany> {
  const res = await apiFetch("/api/admin/companies", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson<PartnerCompany>(res);
}

export async function updatePartnerCompany(
  id: number,
  input: PartnerCompanyInput,
): Promise<PartnerCompany> {
  const res = await apiFetch(`/api/admin/companies/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson<PartnerCompany>(res);
}

export async function deletePartnerCompany(id: number): Promise<void> {
  const res = await apiFetch(`/api/admin/companies/${id}`, { method: "DELETE" });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const message = extractMessage(body) ?? `partner-companies api ${res.status}`;
    throw new PartnerCompanyApiError(res.status, body, message);
  }
}
